/**
 * Données et sécurité : export complet, sauvegardes, restauration.
 *
 * La restauration suit un protocole en deux temps : vérification de l'archive
 * (sans aucune écriture), puis exécution avec confirmation explicite. Une
 * sauvegarde de sécurité est créée automatiquement avant toute écriture.
 */

import { useRef, useState } from "react";

import { ApiError, saveBlob } from "../api/client";
import { dataApi } from "../api/endpoints";
import type { RestoreMode, RestorePreview, RestoreResult } from "../api/types";
import { useSensitiveAction } from "../components/ReauthDialog";
import { useToast } from "../components/ToastContext";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  EmptyState,
  ErrorAlert,
  LoadingBlock,
  PageHeader,
  TextField,
} from "../components/ui";
import { useDocumentTitle, useResource } from "../hooks/useApi";
import { formatDateTime, formatFileSize } from "../lib/format";

const RESTORE_PHRASE = "RESTAURER";

export function DataPage() {
  useDocumentTitle("Données et sauvegardes");
  const toast = useToast();
  const fileInput = useRef<HTMLInputElement>(null);

  const archives = useResource(() => dataApi.archives(), []);
  const format = useResource(() => dataApi.format(), []);

  const [includeDocuments, setIncludeDocuments] = useState(true);
  const [includeAudit, setIncludeAudit] = useState(true);

  const [restoreFile, setRestoreFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<RestorePreview | null>(null);
  const [previewError, setPreviewError] = useState<ApiError | null>(null);
  const [mode, setMode] = useState<RestoreMode>("merge_skip_existing");
  const [confirmation, setConfirmation] = useState("");
  const [result, setResult] = useState<RestoreResult | null>(null);

  /* --- Export immédiat -------------------------------------------------- */
  const exportNow = useSensitiveAction(
    async () => {
      const response = await dataApi.export({
        include_documents: includeDocuments,
        include_audit: includeAudit,
      });
      saveBlob(response);
      toast.success(
        "Export téléchargé",
        "Conservez cette archive dans un emplacement distinct du serveur.",
      );
    },
    { onError: (error) => toast.error("Export impossible", error.message) },
  );

  /* --- Sauvegarde conservée sur le serveur ------------------------------ */
  const createBackup = useSensitiveAction(
    async () => {
      await dataApi.createBackup({
        include_documents: includeDocuments,
        include_audit: includeAudit,
      });
      await archives.reload();
      toast.success("Sauvegarde créée");
    },
    { onError: (error) => toast.error("Sauvegarde impossible", error.message) },
  );

  const [archiveToDownload, setArchiveToDownload] = useState<string | null>(null);
  const downloadArchive = useSensitiveAction(
    async () => {
      if (!archiveToDownload) return;
      saveBlob(await dataApi.downloadArchive(archiveToDownload));
      await archives.reload();
    },
    { onError: (error) => toast.error("Téléchargement impossible", error.message) },
  );

  const [archiveToDelete, setArchiveToDelete] = useState<string | null>(null);
  const deleteArchive = useSensitiveAction(
    async () => {
      if (!archiveToDelete) return;
      await dataApi.deleteArchive(archiveToDelete);
      await archives.reload();
      toast.success("Sauvegarde supprimée du serveur");
    },
    { onError: (error) => toast.error("Suppression impossible", error.message) },
  );

  /* --- Restauration ----------------------------------------------------- */
  const checkArchive = useSensitiveAction(
    async () => {
      if (!restoreFile) return;
      setPreviewError(null);
      setResult(null);
      try {
        setPreview(await dataApi.restorePreview(restoreFile));
      } catch (error) {
        if (error instanceof ApiError && !error.requiresReauth) {
          setPreviewError(error);
          setPreview(null);
        }
        throw error;
      }
    },
    {
      onError: (error) => {
        setPreviewError(error);
        setPreview(null);
      },
    },
  );

  const runRestore = useSensitiveAction(
    async () => {
      if (!restoreFile) return;
      const response = await dataApi.restore(restoreFile, mode, confirmation);
      setResult(response);
      setPreview(null);
      setConfirmation("");
      await archives.reload();
      toast.success("Restauration terminée", response.summary);
    },
    { onError: (error) => toast.error("Restauration refusée", error.message) },
  );

  const canRestore =
    restoreFile !== null &&
    preview !== null &&
    confirmation.trim().toUpperCase() === RESTORE_PHRASE;

  return (
    <>
      <PageHeader
        title="Données et sauvegardes"
        subtitle="Vos données vous appartiennent : vous pouvez les exporter, les sauvegarder et les restaurer à tout moment."
      />

      <div className="stack">
        <Alert tone="info" title="Ces opérations demandent une confirmation d'identité">
          Elles touchent l'ensemble de votre dossier. Votre mot de passe (et votre
          second facteur si vous en avez un) vous sera demandé si votre dernière
          confirmation est trop ancienne.
        </Alert>

        <Card
          title="Contenu des archives"
          hint="S'applique à l'export comme à la sauvegarde."
        >
          <Checkbox
            label="Inclure les justificatifs"
            description="Les fichiers eux-mêmes, en plus de leurs métadonnées. L'archive est plus volumineuse mais complète."
            checked={includeDocuments}
            onChange={setIncludeDocuments}
          />
          <Checkbox
            label="Inclure le journal d'audit"
            description="Trace des connexions, validations, exports et modifications de sécurité."
            checked={includeAudit}
            onChange={setIncludeAudit}
          />
        </Card>

        <div className="grid grid--2">
          <Card
            title="Exporter toutes mes données"
            hint="Téléchargement immédiat, rien n'est conservé sur le serveur."
          >
            <Button
              variant="primary"
              block
              loading={exportNow.pending}
              onClick={() => void exportNow.run()}
            >
              Télécharger l'export complet
            </Button>
            {format.data?.data && (
              <details className="mt-4">
                <summary>Que contient l'archive ?</summary>
                <p className="mt-3">{format.data.message}</p>
                <ul className="mono">
                  {(format.data.data.files as string[]).map((file) => (
                    <li key={file}>{file}</li>
                  ))}
                </ul>
                <p className="inline-note mb-0">
                  Format {String(format.data.data.format)} version{" "}
                  {String(format.data.data.format_version)}.
                </p>
              </details>
            )}
          </Card>

          <Card
            title="Créer une sauvegarde"
            hint="L'archive est conservée sur le serveur et reste téléchargeable."
          >
            <Button
              variant="ghost"
              block
              loading={createBackup.pending}
              onClick={() => void createBackup.run()}
            >
              Créer une sauvegarde maintenant
            </Button>
            <Alert tone="warning" title="Une sauvegarde sur le serveur ne suffit pas">
              Si le serveur est perdu, la sauvegarde l'est aussi. Téléchargez
              régulièrement vos archives et conservez-les ailleurs.
            </Alert>
          </Card>
        </div>

        {/* --- Archives existantes ---------------------------------------- */}
        <Card title="Mes sauvegardes" flush>
          {archives.loading ? (
            <LoadingBlock />
          ) : !archives.data || archives.data.items.length === 0 ? (
            <EmptyState title="Aucune sauvegarde conservée sur le serveur" />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <caption className="visually-hidden">Sauvegardes disponibles</caption>
                <thead>
                  <tr>
                    <th scope="col">Fichier</th>
                    <th scope="col">Créée le</th>
                    <th scope="col">Taille</th>
                    <th scope="col">Contenu</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {archives.data.items.map((archive) => (
                    <tr key={archive.id}>
                      <td>
                        {archive.filename}
                        {archive.kind === "pre_restore" && (
                          <>
                            {" "}
                            <span className="badge badge--info">
                              Avant restauration
                            </span>
                          </>
                        )}
                        {archive.content_sha256 && (
                          <div className="inline-note mono truncate">
                            SHA-256 : {archive.content_sha256.slice(0, 16)}…
                          </div>
                        )}
                      </td>
                      <td className="nowrap">{formatDateTime(archive.created_at)}</td>
                      <td className="numeric nowrap">
                        {formatFileSize(archive.size_bytes)}
                      </td>
                      <td>
                        {archive.item_counts
                          ? `${archive.item_counts.tasks ?? 0} démarches, ${
                              archive.item_counts.documents ?? 0
                            } justificatifs`
                          : "—"}
                        {!archive.includes_documents && (
                          <div className="inline-note">Sans les fichiers</div>
                        )}
                      </td>
                      <td>
                        <div className="btn-group">
                          <Button
                            size="sm"
                            variant="quiet"
                            onClick={() => {
                              setArchiveToDownload(archive.id);
                              void downloadArchive.run();
                            }}
                          >
                            Télécharger
                          </Button>
                          <Button
                            size="sm"
                            variant="quiet"
                            onClick={() => {
                              setArchiveToDelete(archive.id);
                              void deleteArchive.run();
                            }}
                          >
                            Supprimer
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* --- Restauration ----------------------------------------------- */}
        <Card
          title="Restaurer une sauvegarde"
          hint="L'archive est d'abord vérifiée, sans aucune écriture."
        >
          <Alert tone="info" title="Comment la restauration se comporte">
            Par défaut, la restauration <strong>ajoute ce qui manque</strong> et
            n'écrase jamais une donnée existante : un élément déjà présent est
            ignoré et signalé dans le rapport. Une sauvegarde de sécurité est
            créée automatiquement avant toute écriture.
          </Alert>

          <input
            ref={fileInput}
            type="file"
            accept=".zip,application/zip"
            className="visually-hidden"
            onChange={(event) => {
              const file = event.target.files?.[0] ?? null;
              setRestoreFile(file);
              setPreview(null);
              setPreviewError(null);
              setResult(null);
              event.target.value = "";
            }}
          />

          <div className="btn-group mt-4">
            <Button variant="ghost" onClick={() => fileInput.current?.click()}>
              {restoreFile ? "Choisir une autre archive" : "Choisir une archive"}
            </Button>
            {restoreFile && (
              <Button
                variant="primary"
                loading={checkArchive.pending}
                onClick={() => void checkArchive.run()}
              >
                Vérifier cette archive
              </Button>
            )}
          </div>

          {restoreFile && (
            <p className="inline-note mt-3">
              Archive sélectionnée : {restoreFile.name} (
              {formatFileSize(restoreFile.size)})
            </p>
          )}

          {previewError && (
            <Alert tone="critical" title="Archive refusée">
              {previewError.message}
            </Alert>
          )}

          {preview && (
            <div className="mt-4 stack">
              <Alert tone="success" title="Archive valide">
                {preview.summary}
              </Alert>

              <details open>
                <summary>Détail de ce qui serait fait</summary>
                <div className="mt-3">
                  <h3>Éléments qui seraient créés</h3>
                  {Object.keys(preview.created).length === 0 ? (
                    <p className="text-muted">Aucun.</p>
                  ) : (
                    <ul>
                      {Object.entries(preview.created).map(([key, count]) => (
                        <li key={key}>
                          {key} : {count}
                        </li>
                      ))}
                    </ul>
                  )}
                  <h3>Éléments ignorés car déjà présents</h3>
                  {Object.keys(preview.skipped_existing).length === 0 ? (
                    <p className="text-muted">Aucun.</p>
                  ) : (
                    <ul>
                      {Object.entries(preview.skipped_existing).map(([key, count]) => (
                        <li key={key}>
                          {key} : {count}
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="inline-note">
                    Justificatifs présents dans l'archive :{" "}
                    {preview.documents_in_archive}.
                  </p>
                </div>
              </details>

              {preview.warnings.length > 0 && (
                <Alert tone="warning" title="Points d'attention">
                  <ul className="mb-0">
                    {preview.warnings.map((warning) => (
                      <li key={warning}>{warning}</li>
                    ))}
                  </ul>
                </Alert>
              )}

              <fieldset className="fieldset">
                <legend className="fieldset__legend">Stratégie de restauration</legend>
                <label className="radio">
                  <input
                    type="radio"
                    name="mode"
                    checked={mode === "merge_skip_existing"}
                    onChange={() => setMode("merge_skip_existing")}
                  />
                  <span className="checkbox__text">
                    <strong>Ajouter ce qui manque (recommandé)</strong>
                    <span>
                      Les éléments déjà présents sont conservés tels quels. Aucune
                      donnée actuelle n'est modifiée.
                    </span>
                  </span>
                </label>
                <label className="radio">
                  <input
                    type="radio"
                    name="mode"
                    checked={mode === "merge_update_existing"}
                    onChange={() => setMode("merge_update_existing")}
                  />
                  <span className="checkbox__text">
                    <strong>Remplacer par le contenu de l'archive</strong>
                    <span>
                      Les éléments existants sont mis à jour avec les valeurs de
                      l'archive. Vos modifications postérieures à la sauvegarde
                      seront perdues.
                    </span>
                  </span>
                </label>
              </fieldset>

              <TextField
                label={`Saisissez « ${RESTORE_PHRASE} » pour confirmer`}
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                autoComplete="off"
                spellCheck={false}
              />

              <Button
                variant={mode === "merge_update_existing" ? "danger" : "primary"}
                disabled={!canRestore}
                loading={runRestore.pending}
                onClick={() => void runRestore.run()}
              >
                Lancer la restauration
              </Button>
            </div>
          )}

          {result && (
            <div className="mt-4">
              <Alert tone="success" title="Restauration terminée">
                <p>{result.summary}</p>
                {result.safety_backup_archive_id && (
                  <p className="mb-0">
                    Une sauvegarde de l'état précédent a été créée
                    automatiquement : vous la retrouvez dans la liste ci-dessus
                    (« Avant restauration »).
                  </p>
                )}
              </Alert>
            </div>
          )}
        </Card>

        <ErrorAlert error={archives.error} title="Sauvegardes indisponibles" />

        <Card title="Sauvegarde du serveur">
          <p className="text-muted mb-0">
            La sauvegarde que vous créez ici couvre vos données applicatives. La
            sauvegarde complète du serveur (base de données, fichiers,
            configuration) relève de l'administration de l'instance : la
            procédure est décrite dans <span className="mono">docs/BACKUP_RESTORE.md</span>.
          </p>
        </Card>
      </div>

      {exportNow.dialog}
      {createBackup.dialog}
      {downloadArchive.dialog}
      {deleteArchive.dialog}
      {checkArchive.dialog}
      {runRestore.dialog}
    </>
  );
}
